import { useCallback, useMemo, useState } from "react";

function toInputDate(d) {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function endOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

function addDays(d, days) {
  const next = new Date(d);
  next.setDate(next.getDate() + days);
  return next;
}

const PRESETS = [
  {
    key: "current_month",
    label: "Mes actual",
    getRange: () => {
      const now = new Date();
      return { desde: startOfMonth(now), hasta: endOfMonth(now) };
    },
  },
  {
    key: "prev_month",
    label: "Mes anterior",
    getRange: () => {
      const now = new Date();
      const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return { desde: startOfMonth(prev), hasta: endOfMonth(prev) };
    },
  },
  {
    key: "last_24h",
    label: "Últimas 24h",
    getRange: () => {
      const now = new Date();
      return { desde: addDays(now, -1), hasta: now };
    },
  },
  {
    key: "yesterday",
    label: "Ayer",
    getRange: () => {
      const now = new Date();
      const day = addDays(now, -1);
      return { desde: day, hasta: day };
    },
  },
  {
    key: "last_7",
    label: "Últimos 7 días",
    getRange: () => {
      const now = new Date();
      return { desde: addDays(now, -6), hasta: now };
    },
  },
  {
    key: "last_30",
    label: "Últimos 30 días",
    getRange: () => {
      const now = new Date();
      return { desde: addDays(now, -29), hasta: now };
    },
  },
];

export default function useFiltrosInformes() {
  const presets = useMemo(() => PRESETS, []);
  const defaultRange = presets.find((p) => p.key === "last_30")?.getRange() || {
    desde: addDays(new Date(), -29),
    hasta: new Date(),
  };

  const [filters, setFilters] = useState({
    preset: "last_30",
    desde: toInputDate(defaultRange.desde),
    hasta: toInputDate(defaultRange.hasta),
    usuarioIds: [],
  });

  const setPreset = useCallback((key) => {
    const preset = presets.find((p) => p.key === key);
    if (!preset) return;
    const range = preset.getRange();
    setFilters((prev) => ({
      ...prev,
      preset: key,
      desde: toInputDate(range.desde),
      hasta: toInputDate(range.hasta),
    }));
  }, [presets]);

  const setDesde = useCallback((value) => {
    setFilters((prev) => ({ ...prev, preset: "custom", desde: value }));
  }, []);

  const setHasta = useCallback((value) => {
    setFilters((prev) => ({ ...prev, preset: "custom", hasta: value }));
  }, []);

  const setUsuarioIds = useCallback((value) => {
    setFilters((prev) => {
      const nextValue = typeof value === "function" ? value(prev.usuarioIds) : value;
      return { ...prev, usuarioIds: Array.isArray(nextValue) ? nextValue : [] };
    });
  }, []);

  const buildPayload = useCallback(() => {
    const idsRaw = Array.isArray(filters.usuarioIds) ? filters.usuarioIds : [];
    const ids = idsRaw
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));
    const usuarioId = ids.length === 1 ? ids[0] : null;
    const usuarioIds = ids.length ? ids : null;
    return {
      fechaDesde: filters.desde,
      fechaHasta: filters.hasta,
      usuarioId,
      usuarioIds,
    };
  }, [filters]);

  return {
    presets,
    filters,
    setPreset,
    setDesde,
    setHasta,
    setUsuarioIds,
    buildPayload,
  };
}
